import React from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import SurveyQuestionsPanel from './SurveyQuestionsPanel';
import SurveyStatisticsSidebar from './SurveyStatisticsSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {  Settings } from 'lucide-react';

interface SurveyMainContentProps {
  generatedQuestions: any[];
  answers: Record<string, any>;
  mode: string;
  currentGeneration?: any;
  user?: any;
  toolId: string;
  currentLang: string;
  surveyTemplateId?: string;
  surveyTemplate?: any;
  config: any;
  isGenerating: boolean;
  onQuestionChange?: (index: number, field: string, value: any) => void;
  onAnswerChange?: (questionId: string, value: any) => void;
  onAddQuestion?: () => void;
  onDeleteQuestion?: (index: number) => void;
  onDragEnd?: (result: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasChanges?: boolean;
  onGenerateQuestions: () => void;
  onCopyAllQuestions: () => void;
  onExportQuestions: (format: 'json' | 'csv') => void;
  onShowContactDialog: () => void;
}

const SurveyMainContent: React.FC<SurveyMainContentProps> = ({
  generatedQuestions,
  answers,
  mode,
  currentGeneration,
  user,
  toolId,
  currentLang,
  surveyTemplateId,
  surveyTemplate,
  config,
  isGenerating,
  onQuestionChange,
  onAnswerChange,
  onAddQuestion,
  onDeleteQuestion,
  onDragEnd,
  onSave,
  isSaving,
  hasChanges,
  onGenerateQuestions,
  onCopyAllQuestions,
  onExportQuestions,
  onShowContactDialog
}) => {
  return (
    <>
      {/* Desktop / Tablet: Keep resizable layout */}
      <div className="hidden md:block">
        <ResizablePanelGroup direction="horizontal" className="min-h-[800px] rounded-lg overflow-hidden bg-white/95 backdrop-blur-sm shadow-elegant">
          {/* Questions Panel */}
          <ResizablePanel defaultSize={70} minSize={50}>
            <SurveyQuestionsPanel
              generatedQuestions={generatedQuestions}
              answers={answers}
              mode={mode}
              currentGeneration={currentGeneration}
              user={user}
              toolId={toolId}
              currentLang={currentLang}
              onQuestionChange={onQuestionChange}
              onAnswerChange={onAnswerChange}
              onAddQuestion={onAddQuestion}
              onDeleteQuestion={onDeleteQuestion}
              onDragEnd={onDragEnd}
              onSave={onSave}
              isSaving={isSaving}
              hasChanges={hasChanges}
              onShowContactDialog={onShowContactDialog}
            />
          </ResizablePanel>
          
          <ResizableHandle withHandle className="w-1 bg-border/30 hover:bg-border/50 transition-colors" />
          
          {/* Statistics Sidebar */}
          <ResizablePanel defaultSize={30} minSize={25} maxSize={40}>
            <SurveyStatisticsSidebar
              config={config}
              generatedQuestions={generatedQuestions}
              answers={answers}
              surveyTemplateId={surveyTemplateId}
              surveyTemplate={surveyTemplate}
              currentGeneration={currentGeneration}
              isGenerating={isGenerating}
              mode={mode}
              toolId={toolId}
              currentLang={currentLang}
              onGenerateQuestions={onGenerateQuestions}
              onCopyAllQuestions={onCopyAllQuestions}
              onExportQuestions={onExportQuestions}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Mobile: Full-width questions with top-bar trigger opening stats in a Sheet */}
      <div className="md:hidden relative min-h-[700px] rounded-lg overflow-hidden bg-white/95 backdrop-blur-sm shadow-elegant">
        <Sheet>
          <SurveyQuestionsPanel
            generatedQuestions={generatedQuestions}
            answers={answers}
            mode={mode}
            currentGeneration={currentGeneration}
            user={user}
            toolId={toolId}
            currentLang={currentLang}
            onQuestionChange={onQuestionChange}
            onAnswerChange={onAnswerChange}
            onAddQuestion={onAddQuestion}
            onDeleteQuestion={onDeleteQuestion}
            onDragEnd={onDragEnd}
            onSave={onSave}
            isSaving={isSaving}
            hasChanges={hasChanges}
            onShowContactDialog={onShowContactDialog}
            mobileStatsTrigger={
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Options
                </Button>
              </SheetTrigger>
            }
          />

          <SheetContent side="right" className="p-0">
            <div className="h-full overflow-y-auto">
              <SurveyStatisticsSidebar
                config={config}
                generatedQuestions={generatedQuestions}
                answers={answers}
                surveyTemplateId={surveyTemplateId}
                surveyTemplate={surveyTemplate}
                currentGeneration={currentGeneration}
                isGenerating={isGenerating}
                mode={mode}
                toolId={toolId}
                currentLang={currentLang}
                onGenerateQuestions={onGenerateQuestions}
                onCopyAllQuestions={onCopyAllQuestions}
                onExportQuestions={onExportQuestions}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};

export default SurveyMainContent; 